import React from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {TableSectionWrapper, CollapsibleSectionWrapper} from '../money/work_and_expenses/TableSection.styled';
import {LockIcon, Table, Collapsible, CustomScrollDiv} from '@/web-components';
import {UnlockIcon} from '@/web-components/design-icons';
import {useIntl} from 'react-intl';
import getAggregateRowTitle from '../util/UseAggregateIntl';
import RevenueRecognitionTableRow from './RevenueRecognitionTableRow';
import {LoadMore} from '../../../../loaders/LoadMore';
import RevenueRecognitionLoader, {revenueRecognitionLoaderQuery} from '../loaders/RevenueRecognitionLoader';
import {filterAggregateFields, TABLE_TYPE, isColumnLocked} from '../util/BudgetUtils';
import {SPECIAL_ROLE, BUDGET_TYPE} from '../../../../../constants';
import {RevenueRecognitionTableContainer} from './RevenueRecognition.styled';
import {
	HalfLockIconContainer,
	LockIconContainer,
	LockHeaderContainer,
	HeaderContainer,
	HeaderText,
} from './RevenueRecognition.styled';
import {showFixedPriceLockModal, showFixedPriceUnlockModal} from '../util/PeriodLocks';

const FP_CHILD_AGGREGATES = ['fixedPriceDeliverablesTime', 'fixedPriceDeliverablesExpenses'];
const EXPANDABLE_AGGREGATES = ['timeAndMaterials', 'fixedPriceDeliverables'];

const RevenueRecognition = ({project, retry, timestamp, hideLocking, hasExpenses}) => {
	const intl = useIntl();
	const {formatMessage} = intl;

	const aggregates = filterAggregateFields(project, [
		'recognitionAmount',
		'estimatedCost',
		'recognitionProfit',
		'recognitionProfitPercentage',
	]);

	const searchQuery = {
		filters: [
			{
				field: 'ROLE',
				operator: 'NOT_IN',
				value: SPECIAL_ROLE.EXPENSE,
			},
		],
	};
	const expenseSearchQuery = {
		filters: [
			{
				field: 'ROLE',
				operator: 'IN',
				value: SPECIAL_ROLE.EXPENSE,
			},
		],
	};

	const pricedDeliverables =
		project.budgetType === BUDGET_TYPE.TIME_AND_MATERIALS
			? project.deliverables.edges.map(edge => edge.node).filter(deliverable => deliverable.price > 0)
			: [];
	const hasDeliverables = pricedDeliverables.length > 0;
	const pricedDeliverableIds = pricedDeliverables.map(deliverable => deliverable.id);
	const deliverableNamesById = pricedDeliverables.reduce((map, deliverable) => {
		map[deliverable.id] = deliverable.name;
		return map;
	}, {});

	return (
		<CollapsibleSectionWrapper>
			<Collapsible
				title={formatMessage({id: 'project_budget.revenue_recognition'})}
				initiallyCollapsed={false}
				localStorageKey={'project_budget.collapse_revenue_recognition_table'}
				cy={'revenue-recognition-collapse'}
			>
				<TableSectionWrapper>
					<LoadMore
						key="query-render-revenue-recognition"
						query={revenueRecognitionLoaderQuery}
						loader={
							<RevenueRecognitionLoader
								aggregateLevel={'MONTH'}
								aggregates={aggregates}
								expenseAggregates={[
									'billableTotalTimeAndExpensesAtCompletion',
									'allTotalTimeAndExpensesAtCompletion',
								]}
								retry={retry}
								timestamp={timestamp}
								hasExpenses={hasExpenses}
								hasDeliverables={hasDeliverables}
								pricedDeliverableIds={pricedDeliverableIds}
							/>
						}
						variables={{
							projectId: project.id,
							startYear: project.projectRevRecFirstDateYear,
							startMonth: project.projectRevRecFirstDateMonth,
							startDay: project.projectRevRecFirstDateDay,
							endYear: project.projectRevRecLastDateYear,
							endMonth: project.projectRevRecLastDateMonth,
							endDay: project.projectRevRecLastDateDay,
							aggregateLevel: 'MONTH',
							aggregates: aggregates,
							searchQuery: searchQuery,
							expenseAggregates: [
								'billableTotalTimeAndExpensesAtCompletion',
								'allTotalTimeAndExpensesAtCompletion',
							],
							expenseSearchQuery: expenseSearchQuery,
							hasExpenses: hasExpenses,
							hasDeliverables: hasDeliverables,
						}}
					>
						{({data}) => {
							const recognitionTotalAmounts = {
								time: (hasExpenses
									? data.totalTimeDataArray
									: data.rows.find(row => row.aggregate === 'recognitionTotal').dataArray
								).map((month, index) => {
									return {startDate: data.columns[index].startDate, amount: month.value};
								}),
								total: data.rows
									.find(row => row.aggregate === 'recognitionTotal')
									.dataArray.map((month, index) => {
										return {startDate: data.columns[index].startDate, amount: month.value};
									}),
							};
							return (
								<CustomScrollDiv
									horizontalContent
									autoHeight={true}
									autoHeightMin={data.rows.length * 48 + 53 + 15}
								>
									<RevenueRecognitionTableContainer>
										<Table paddedCells cy="budget-details">
											<Table.Header>
												<Table.HeaderColumn visible flex={1} width="EXTRA_LARGE" />
												{data.columns.map((column, index) => (
													<Table.HeaderColumn
														key={formatMessage({id: 'common.revenue_recognition'}) + column.name}
														usePadding
														align="right"
														visible={true}
														width="MEDIUM_SMALL"
													>
														<Table.HeaderColumn.Title bold={index === data.columns.length - 1}>
															{hideLocking || !column.startDate ? (
																<HeaderContainer>
																	{isColumnLocked(project.fixedPriceLocks, column) ? (
																		<LockIcon
																			color={'#C4C4CC'}
																			size={LockIcon.SIZE.STANDARD}
																		/>
																	) : null}
																	<HeaderText>{column.name}</HeaderText>
																</HeaderContainer>
															) : (
																<LockHeaderContainer
																	onClick={() =>
																		isColumnLocked(project.fixedPriceLocks, column)
																			? showFixedPriceUnlockModal(
																					project,
																					column.startDate.format('YYYY-MM'),
																					recognitionTotalAmounts,
																					data.rows[0].currency
																			  )
																			: showFixedPriceLockModal(
																					project,
																					column.startDate.format('YYYY-MM'),
																					recognitionTotalAmounts
																			  )
																	}
																>
																	{isColumnLocked(project.fixedPriceLocks, column) ? (
																		<>
																			<HalfLockIconContainer>
																				<UnlockIcon
																					size="small"
																					color={'#C4C4CC'}
																					hoverColor={null}
																				/>
																			</HalfLockIconContainer>
																			<LockIconContainer>
																				<LockIcon
																					color={'#C4C4CC'}
																					size={LockIcon.SIZE.STANDARD}
																				/>
																			</LockIconContainer>
																		</>
																	) : (
																		<HalfLockIconContainer>
																			<UnlockIcon
																				size="small"
																				color={'#C4C4CC'}
																				hoverColor={null}
																			/>
																		</HalfLockIconContainer>
																	)}
																	<HeaderText>{column.name}</HeaderText>
																</LockHeaderContainer>
															)}
														</Table.HeaderColumn.Title>
													</Table.HeaderColumn>
												))}
											</Table.Header>
											<Table.Rows data={data} canExpand={hasDeliverables}>
												{({rowData, tableColumnsProps, expanded, nextLevelProps: childLevelProps}) => {
													// rowData.children is present only for timeAndMaterials and fixedPriceDeliverables rows
													// that have a time/expense breakdown to display; other rows have no children to expand.
													const rowCanExpand =
														EXPANDABLE_AGGREGATES.includes(rowData.aggregate) && rowData.children
															? true
															: undefined;
													return (
														<>
															<RevenueRecognitionTableRow
																data={data}
																rowData={rowData}
																tableColumnProps={tableColumnsProps}
																project={project}
																canExpand={rowCanExpand}
																headerColumn={
																	<Table.Column>
																		{getAggregateRowTitle(
																			intl,
																			rowData.aggregate,
																			TABLE_TYPE.REVENUE_RECOGNITION
																		)}
																	</Table.Column>
																}
															/>
															{expanded && rowData.children && (
																<Table.Rows
																	data={{rows: rowData.children, columns: data.columns}}
																	{...childLevelProps}
																	canExpand={true}
																>
																	{({
																		rowData: childRow,
																		tableColumnsProps: childColumnProps,
																		expanded: childExpanded,
																		nextLevelProps: deliverableLevelProps,
																	}) => {
																		const childCanExpand =
																			FP_CHILD_AGGREGATES.includes(childRow.aggregate) &&
																			childRow.deliverables &&
																			childRow.deliverables.length > 0
																				? true
																				: undefined;
																		return (
																			<>
																				<RevenueRecognitionTableRow
																					data={data}
																					rowData={childRow}
																					tableColumnProps={childColumnProps}
																					project={project}
																					canExpand={childCanExpand}
																					headerColumn={
																						<Table.Column>
																							{getAggregateRowTitle(
																								intl,
																								childRow.aggregate,
																								TABLE_TYPE.REVENUE_RECOGNITION
																							)}
																						</Table.Column>
																					}
																				/>
																				{childExpanded && childRow.deliverables && (
																					<Table.Rows
																						data={{
																							rows: childRow.deliverables,
																							columns: data.columns,
																						}}
																						{...deliverableLevelProps}
																						canExpand={true}
																					>
																						{({
																							rowData: deliverableRow,
																							tableColumnsProps:
																								deliverableColumnProps,
																						}) => (
																							<RevenueRecognitionTableRow
																								data={data}
																								rowData={{
																									...deliverableRow,
																									aggregate:
																										deliverableRow.deliverableId,
																								}}
																								tableColumnProps={
																									deliverableColumnProps
																								}
																								project={project}
																								canExpand={undefined}
																								headerColumn={
																									<Table.Column>
																										{deliverableNamesById[
																											deliverableRow
																												.deliverableId
																										] ||
																											deliverableRow.deliverableId}
																									</Table.Column>
																								}
																							/>
																						)}
																					</Table.Rows>
																				)}
																			</>
																		);
																	}}
																</Table.Rows>
															)}
														</>
													);
												}}
											</Table.Rows>
										</Table>
									</RevenueRecognitionTableContainer>
								</CustomScrollDiv>
							);
						}}
					</LoadMore>
				</TableSectionWrapper>
			</Collapsible>
		</CollapsibleSectionWrapper>
	);
};

export default createFragmentContainer(RevenueRecognition, {
	project: graphql`
		fragment RevenueRecognition_project on ProjectType {
			id
			budgetType
			deliverables(first: 1000) {
				edges {
					node {
						id
						name
						price
					}
				}
			}
			billable
			defaultPeriodBudgetType
			projectFirstDateYear
			projectFirstDateMonth
			projectFirstDateDay
			projectLastDateYear
			projectLastDateMonth
			projectLastDateDay
			projectRevRecFirstDateYear
			projectRevRecFirstDateMonth
			projectRevRecFirstDateDay
			projectRevRecLastDateYear
			projectRevRecLastDateMonth
			projectRevRecLastDateDay
			estimationUnit
			fixedPriceLocks(first: 1000) @connection(key: "Project_fixedPriceLocks", filters: []) {
				edges {
					node {
						id
						locked
						startDate
						endDate
					}
				}
			}
			rateCard {
				currency
			}
		}
	`,
});
